import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import { LocaleCurrency } from "helpers/Locale";
import { IOrderExchange, IOrderTransaction } from "redux/reducers/models/order";
import OrderLineItemBlock from "./OrderLineItemBlock";
import OrderTenderRows from "./OrderTenderRows";
import { exchangeReplacementItems, exchangeReturnedItems, exchangeTenders } from "./orderExchangeDisplay";

interface IOrderExchangeCardProps {
  exchange: IOrderExchange;
  currency?: string;
  discountLines?: Array<any>;
  transactions?: IOrderTransaction[];
  onOpenTransaction: (transaction: IOrderTransaction) => void;
  onGiftCardClick?: () => void;
  onTicketDownload?: (lineItemId: number, format?: "zebra") => void;
  onTicketPrint?: (lineItemId: number) => void;
}

export default function OrderExchangeCard(props: IOrderExchangeCardProps) {
  const { t } = useTranslation();
  const { exchange, currency } = props;
  const returnedItems = exchangeReturnedItems(exchange);
  const purchasedItems = exchangeReplacementItems(exchange);
  const tenders = exchangeTenders(props.transactions, exchange);
  const net = Number(exchange.net_balance ?? 0);
  const processedLabel = exchange.processed_at
    ? moment.utc(exchange.processed_at).local().format("MMM D, YYYY h:mm A")
    : null;

  const settlementLabel =
    exchange.settlement_type === "refund"
      ? t("Refund due")
      : exchange.settlement_type === "collect"
        ? t("Amount due")
        : t("Even exchange");

  return (
    <Card title={processedLabel ? `${t("Exchange")} · ${processedLabel}` : t("Exchange")}>
      {returnedItems.length > 0 ? (
        <Card.Section title={t("Returned")}>
          {returnedItems.map(item => (
            <Card.SubSection key={item.id}>
              <div className="order-line-item">
                <div>
                  <p className="text-lg text-semibold">{item.product_title}</p>
                  {item.variant_title && item.variant_title !== item.product_title ? (
                    <p className="text-sm text-gray-500">{item.variant_title}</p>
                  ) : null}
                </div>
                <div className="text-right">
                  <p className="text-lg text-gray-500">{item.quantity} x</p>
                </div>
                <div className="text-right">
                  <p className="text-lg text-semibold">
                    -<LocaleCurrency currency={currency} amount={item.subtotal_price} />
                  </p>
                </div>
              </div>
            </Card.SubSection>
          ))}
        </Card.Section>
      ) : null}

      {purchasedItems.length > 0 ? (
        <Card.Section title={t("Purchased")}>
          {purchasedItems.map(item => (
            <Card.SubSection key={item.id}>
              <OrderLineItemBlock
                item={item}
                currency={currency}
                discountLines={props.discountLines}
                onGiftCardClick={props.onGiftCardClick}
                onTicketDownload={props.onTicketDownload}
                onTicketPrint={props.onTicketPrint}
              />
            </Card.SubSection>
          ))}
        </Card.Section>
      ) : null}

      <Card.Section>
        <div className="order-totals-container">
          <div className="order-totals">
            <span className="text-lg text-semibold">{t("Return total")}</span>
            <p className="text-lg text-semibold">
              -<LocaleCurrency currency={currency} amount={exchange.return_total} />
            </p>
            <span className="text-lg text-semibold">{t("Replacement total")}</span>
            <p className="text-lg text-semibold">
              <LocaleCurrency currency={currency} amount={exchange.exchange_total} />
            </p>
            <span className="text-lg text-semibold">{settlementLabel}</span>
            <p className="text-lg text-semibold">
              {net < 0 ? <span>- </span> : null}
              <LocaleCurrency currency={currency} amount={Math.abs(net)} />
            </p>
            <OrderTenderRows
              transactions={tenders}
              currency={currency}
              onOpenTransaction={props.onOpenTransaction}
            />
          </div>
        </div>
      </Card.Section>
    </Card>
  );
}
